'use client';

import ChangePassword from '@/components/signin/change-password';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAppDispatch } from '@/lib/client-hook/use-app-dispatch';
import { useDictionary } from '@/lib/context/locale-provider';
import { getCookie, setCookie } from '@/lib/cookies';
import { Languages } from '@/lib/dictionaries';
import { setErrorMessage } from '@/slice/error';
import { zodResolver } from '@hookform/resolvers/zod';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import React from 'react';

import '@/app/input.css';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const loginScheme = z.object({
  email: z.string().email({ message: 'Please enter a valid email' }),
  password: z.string().min(1, 'Password must be at least 6 characters'),
});

function LoginEmail({ lang }: { lang: Languages }) {
  const dict = useDictionary();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const { register, handleSubmit } = useForm<z.infer<typeof loginScheme>>({
    resolver: zodResolver(loginScheme),
  });

  const handleSignIn = async (values: z.infer<typeof loginScheme>) => {
    const email = values.email;
    const cookieKey = `login-email-${email}`;

    // 로그인 실패 횟수 읽기
    let failedAttempts = parseInt(getCookie(cookieKey) || '0', 10);

    if (failedAttempts >= 10) {
      dispatch(setErrorMessage(dict!.login.todayNotLoginEmail));
      return;
    }

    const res = await signIn('credentials', {
      email: values.email,
      password: values.password,
      redirect: false,
    });

    if (!res || !res.ok) {
      failedAttempts += 1;
      setCookie(cookieKey, failedAttempts.toString());

      dispatch(
        setErrorMessage(
          dict!.login.errorEmailPassword.replace('{count}', failedAttempts),
        ),
      );
      return;
    }

    router.replace(`/${lang}`);
  };

  const handleSignUp = () => {
    router.replace(`${process.env.NEXT_PUBLIC_API_URL}/${lang}/email-signup`);
  };

  return (
    <div className="w-full flex flex-col items-center justify-center mt-[30px]">
      <form
        className="w-full flex flex-col max-w-[360px] gap-2.5"
        onSubmit={handleSubmit(handleSignIn)}
      >
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="email">{dict!.common.email}</Label>
          <Input
            type="email"
            placeholder={dict!.placeholder.email}
            {...register('email')}
          />
        </div>
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="password">{dict!.common.password}</Label>
          <Input
            type="password"
            placeholder={dict!.placeholder.password}
            {...register('password')}
          />
        </div>
        <button className="mt-2.5 w-[360px] h-[50px] bg-gradient-to-r from-[#FF7395] to-[#70A1F3] rounded-[10px] text-white button01_700">
          LOGIN
        </button>
      </form>
      <div className="mt-[30px] flex flex-row gap-4 items-center justify-center">
        <ChangePassword lang={lang} />
        <Separator orientation="vertical" className="h-[10px]" />
        <button className="underline underline-offset-2" onClick={handleSignUp}>
          {dict!.signup.email}
        </button>
      </div>
    </div>
  );
}

export default LoginEmail;
